import { getMetadata, getThumbnails } from "video-metadata-thumbnails";
import {
  FINAL_VIDEO_DURATION_LIMIT,
  MIN_VIDEO_DURATION_SEC,
  VIDEO_MAX_DURATION_SEC,
  VIDEO_TIMELINE_MAX_SEC,
  VideoMimeTypes,
} from "../../utils/appConstants";

/** Target width of one filmstrip cell in the timeline (px). */
export const TIMELINE_THUMB_CELL_WIDTH = 56;

/** Shared filmstrip height for timeline clips and trim modal (px). */
export const TIMELINE_THUMB_HEIGHT = 90;

export const getVideoClipDuration = (effect: {
  duration?: number;
  metaData?: { startTime?: number; endTime?: number };
}) => {
  const startTime = effect.metaData?.startTime;
  const endTime = effect.metaData?.endTime;

  if (startTime != null && endTime != null) {
    return Math.max(0, endTime - startTime);
  }

  return effect.duration || 0;
};

export const getVideoClipTimelineWidthPercent = (durationSec: number) => {
  if (!durationSec || durationSec <= 0) return 0;
  return Math.min(100, (durationSec / VIDEO_TIMELINE_MAX_SEC) * 100);
};

/** More frames for longer clips so the filmstrip looks like a real timeline preview. */
export const getTimelineThumbnailCount = (durationSec: number) => {
  const duration = Math.max(MIN_VIDEO_DURATION_SEC, durationSec || 0);
  return Math.max(3, Math.min(16, Math.ceil(duration / 2)));
};

export const generateTimelineThumbnails = async (
  source: Blob | File,
  durationSec: number,
  startTime = 0
) => {
  const clipDuration = Math.max(MIN_VIDEO_DURATION_SEC, durationSec || 0);
  const count = getTimelineThumbnailCount(clipDuration);
  const rangeEnd = startTime + clipDuration;

  const thumbnails = await getThumbnails(source, {
    start: startTime,
    end: rangeEnd,
    quality: 0.65,
    interval: clipDuration / count,
  });

  return thumbnails.slice(0, count);
};

export type TimelineClip = {
  uuid: string;
  blob: Blob | File;
  duration?: number;
  source?: "record" | "upload";
  metaData?: { startTime?: number; endTime?: number };
};

export const isRecordedVideoClip = (clip: { source?: string }) =>
  clip?.source === "record";

export const isSupportedVideoUpload = (file: File) => {
  const mimeType = (file.type || "").toLowerCase();
  const fileName = (file.name || "").toLowerCase();

  if (VideoMimeTypes.includes(mimeType)) {
    return true;
  }

  return /\.(mp4|mov)$/i.test(fileName);
};

export const getClipVideoDuration = async (source: Blob | File) => {
  try {
    const metadata = await getMetadata(source);
    const duration = Number(metadata?.duration);

    if (Number.isFinite(duration) && duration > 0) {
      return duration;
    }
  } catch (error) {
    // Fall back to native video metadata loading below.
  }

  return new Promise<number>((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    const objectUrl = URL.createObjectURL(source);

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(objectUrl);
      const duration = Number(video.duration);

      if (Number.isFinite(duration) && duration > 0) {
        resolve(duration);
        return;
      }

      reject(new Error("Unable to read video duration"));
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read uploaded video"));
    };

    video.src = objectUrl;
  });
};

export const getTimelineTotalSeconds = (clips: TimelineClip[]) =>
  clips.reduce((total, clip) => total + getVideoClipDuration(clip), 0);

export const isWithinVideoDurationLimit = (totalSeconds: number) =>
  totalSeconds <= FINAL_VIDEO_DURATION_LIMIT;

export const getVideoDurationLimitMessage = () =>
  `Video duration must be ${VIDEO_MAX_DURATION_SEC} seconds or less`;

export const getRemainingVideoDurationSec = (clips: TimelineClip[]) =>
  Math.max(0, FINAL_VIDEO_DURATION_LIMIT - getTimelineTotalSeconds(clips));

export const wouldExceedVideoDurationLimit = (
  clips: TimelineClip[],
  additionalSeconds = 0
) =>
  getTimelineTotalSeconds(clips) + additionalSeconds >
  FINAL_VIDEO_DURATION_LIMIT;

/** Build ordered merge payload for preview/export from all timeline clips. */
export const buildTimelineMergePayload = (clips: TimelineClip[]) => {
  let totalSeconds = 0;
  const videoMetaData: Array<{
    video: string;
    startTime: number;
    endTime: number;
    mirror: boolean;
  }> = [];

  clips.forEach((clip) => {
    const trimmedDuration = getVideoClipDuration(clip);
    const startTime = clip.metaData?.startTime ?? 0;
    const endTime =
      clip.metaData?.endTime != null
        ? clip.metaData.endTime
        : clip.duration || 0;

    totalSeconds += trimmedDuration;
    videoMetaData.push({
      video: clip.uuid,
      startTime,
      endTime,
      mirror: isRecordedVideoClip(clip),
    });
  });

  return {
    totalSeconds: Math.floor(totalSeconds),
    videoMetaData,
  };
};

export const appendTimelineClipsToFormData = (
  formData: FormData,
  clips: TimelineClip[]
) => {
  clips.forEach((clip) => {
    const fileName =
      (clip.blob as File).name ||
      (clip.blob as Blob & { name?: string }).name ||
      `${clip.uuid}.mp4`;
    formData.append(clip.uuid, clip.blob, fileName);
  });
};
